import { useApi } from "@/hooks";
import { Actions, Events } from "@Context/common/general/context";
import storage from "@Context/storage";
import AccountAuthorizations from "@Enums/AccountAuthorizations";
import useGlobalContext from "@hooks/common/general/use-global-context";
import * as Sentry from "@sentry/react";
import AccountRoles from "@Shared/enums/AccountRoles";
import { Api } from "@Shared/schemas";
import { GetAccountResponse, RoleRecord } from "@Shared/schemas/accounts/accountSchemas";
import { includes, isNumber } from "lodash-es";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const SELECTED_ACCOUNT_KEY = 'selected_account';

const isMissingAccountRecordError = (error: any) =>
    error?.code === "functions/not-found" && error?.details?.reason === "account-record-missing";

interface AccountServiceState {
    isInitialized: boolean;
    roles: RoleRecord[];
    userId: number | null;
}

// Hoist userDBId to the top level to avoid re-renders
let userDBId: number | null = null;

const useAccountSwitching = () => {
    const { dispatch, updateGlobalState } = useGlobalContext();
    const [roles, setRoles] = useState<RoleRecord[]>([]);
    const { call: getRoles, data: rolesData} = useApi({ api: Api.Account.GetRoles, onSuccess: ({data})=> setRoles(data) });
    const { call: getAccounts } = useApi({ api: Api.Account.GetAccounts });

    const ensureRoles = async (): Promise<RoleRecord[]> => {
        if (roles.length === 0) {
            const { data } = await getRoles();
            // Return the data directly instead of waiting for state update
            return data;
        }
        return roles;
    };

    const applySwitchAccount = async (account: GetAccountResponse, accountsData: GetAccountResponse[]) => {
        try {
            // Get the current roles, either from state or API
            const currentRoles = await ensureRoles();
            const hasMissingAccountAuthorizations = !account.authorizations || account.authorizations.length === 0;

            let hasAccessSubscriptions = false;
            let hasPlaylistCMSAccess = false;
            account.authorizations?.forEach((auth: any) => {
                if(!hasAccessSubscriptions) {
                    hasAccessSubscriptions = includes(Object.values(AccountAuthorizations), auth.name) && auth.is_active;
                }
                if(!hasPlaylistCMSAccess) {
                    hasPlaylistCMSAccess = includes(Object.values(AccountAuthorizations), AccountAuthorizations.CUSTOM_PLAYLISTS) && auth.is_active;
                }
            });

            const memberRole = account.members?.find(
                member => member.user_id === userDBId
            );

            const roleName = currentRoles.find(role => role.id === memberRole?.role_id)?.name as AccountRoles | null;

            storage.set({
                key: SELECTED_ACCOUNT_KEY,
                data: { id: account.id },
                session_only: false
            });

            updateGlobalState({
                account: {
                    id: account.id,
                    name: account.name,
                    created_at: account.created_at,
                    updated_at: account.updated_at,
                    stripe_customer_id: account.stripe_customer_id,
                    is_business: account.is_business,
                    user_role: roleName,
                    total_seats: account.total_seats,
                    assigned_seats: account.assigned_seats,
                    available_seats: account.available_seats,
                    tracking_providers: account.tracking_providers || [],
                    authorizations: account.authorizations,
                    data: account,
                    accounts_data: accountsData,
                    is_resolved: true
                },
                user: {
                    noAccount: false,
                    needsAccountProductsRecovery: hasMissingAccountAuthorizations,
                    hasAccessSubscriptions,
                    hasAllAccessSubscription: hasAccessSubscriptions,
                    hasPlaylistCMSAccess
                },
            });
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error applying account switch:", error);
            updateGlobalState({ account: { is_resolved: true } });
        }
    };

    const handleSwitchAccount = async ({ account_id }: { account_id: number }) => {
        try {
            await ensureRoles();

            const { data: accountsData } = await getAccounts({
                resolve_members: true,
                resolve_authorizations: true,
                resolve_products: true
            });
            
            const account = accountsData?.find(acc => acc.id === account_id);

            if (!account) {
                throw new Error(`Account not found: ${account_id}`);
            }

            await applySwitchAccount(account, accountsData);

            // Route to root path if we're not already on it - this is to prevent redirects from happening when the user is already on the root path
            if (!includes(['/', '/home'], window.location.pathname)) {
                Events.app.navigate.emit({ path: "/" })
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error switching account:", error);
        }
    };

    return {
        handleSwitchAccount,
        applySwitchAccount,
        roles
    };
};

const AccountService = (): JSX.Element => {
    const { globalState, dispatch } = useGlobalContext();

    const { call: getAccounts } = useApi({ api: Api.Account.GetAccounts });
    const { handleSwitchAccount, applySwitchAccount } = useAccountSwitching();
    const navigateHook = useNavigate();

    const validateAndLoadAccount = async ({ onSuccess = () => {} }: { onSuccess?: () => void } = {}) => {
        if (!globalState.user.authenticated) {
            return;
        }

        try {
            const storedAccount = storage.get({ key: SELECTED_ACCOUNT_KEY }) as { id: number } | null;
            const { data: accounts } = await getAccounts({
                resolve_members: true,
                resolve_authorizations: true,
                resolve_products: true
            });

            if (!accounts || accounts.length === 0) {
                throw new Error("No accounts found for user");
            }

            if (storedAccount?.id) {
                const validAccount = accounts.find(account => account.id === storedAccount.id);
                if (validAccount) {
                    await applySwitchAccount(validAccount, accounts);
                    onSuccess?.();
                    return;
                }
                storage.remove({ key: SELECTED_ACCOUNT_KEY });
            } 

            if (accounts.length > 1) {
                Sentry.captureException("No account selected, redirecting to select-account", {
                    level: "error",
                    extra: {
                        user_id: userDBId
                    }
                });
                navigateHook("/select-account", { replace: true });
            } else if (accounts.length === 1) {
                await applySwitchAccount(accounts[0], accounts);
            }

            onSuccess?.();
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error validating account:", error);
            const isMissingAccountRecord = isMissingAccountRecordError(error);

            if (isMissingAccountRecord) {
                storage.remove({ key: SELECTED_ACCOUNT_KEY });
            }

            resetAccount({
                user: {
                    noAccount: isMissingAccountRecord,
                    needsAccountProductsRecovery: false,
                    hasAccessSubscriptions: false,
                    hasAllAccessSubscription: false,
                    hasPlaylistCMSAccess: false,
                }
            });
        }
    };


    const resetAccount = (payload?: any) => {
        dispatch({
            type: Actions.CoreActions.UpdateState,
            payload: {
                account: {
                    id: null,
                    name: null,
                    stripe_customer_id: null,
                    is_business: false,
                    authorizations: [],
                    data: null,
                    accounts_data: null,
                    user_role: null,
                    total_seats: 0,
                    assigned_seats: 0,
                    available_seats: 0,
                    tracking_providers: [],
                    is_resolved: false,
                },
                user: {
                    noAccount: false,
                    needsAccountProductsRecovery: false,
                },
                ...payload
            }
        });
    }

    useEffect(() => {
        if (!globalState.user.authenticated) {
            userDBId = null;
            return;
        }

        if(isNumber(globalState.user?.dbId) && userDBId !== globalState.user?.dbId) {
            userDBId = globalState.user?.dbId;
            validateAndLoadAccount();
        }
    }, [globalState.user.authenticated, globalState.user?.dbId]);

    useEffect(() => {
        Events.account.switchAccount.uniq(handleSwitchAccount);
        Events.account.refreshCurrentAccount.uniq(validateAndLoadAccount);
        Events.account.resetAccountState.uniq(resetAccount);
        
        return () => {
            userDBId = null;
            Events.account.switchAccount.remove(handleSwitchAccount);
            Events.account.refreshCurrentAccount.remove(validateAndLoadAccount);
            Events.account.resetAccountState.remove(resetAccount);
        };
    }, []);

    return <div id="ACCOUNT-SERVICE" />;
};

export default AccountService;